import React, { useEffect, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileText, AiOutlineDownload, AiOutlineDelete } from "react-icons/ai";
import { FiFile, FiMoreVertical } from "react-icons/fi";
import Loader from "../../../../../components/shared/loader";
import { deleteMedia } from "../../../../../services/upload-service";
import ConfirmationModal from "../../../../../components/shared/confirmation-modal";
import Modal from "../../../../../components/shared/modal";

const FileDetails = ({ file, onClose, setMedia, accountId, onRemoveImage,collapsed }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    console.log(file)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleActionClick = (rowId) => {
        setOpenMenuId(openMenuId === rowId ? null : rowId);
    };

    const getFileType = () => {
        if (!file) return "";
        if (file?.fileType?.startsWith("image/")) return "Image";
        if (file?.fileType === "application/pdf") return "PDF Document";
        if (file?.fileType?.includes("word")) return "Word Document";
        if (file?.fileType?.includes("excel") || file?.fileType?.includes("spreadsheet")) return "Excel Document";
        if (file?.fileType?.includes("text")) return "Text Document";
        return "Document";
    };

    const getFileIcon = () => {
        if (!file) {
            return (
                <div className="w-full h-40 flex items-center justify-center text-gray-400">
                    <FiFile className="text-6xl" />
                </div>
            );
        }

        if (file?.fileType?.startsWith("image/")) {
            return (
                <img
                    src={file.signedUrl || file.signedThumbUrl || "/fallback-image.png"}
                    alt={file.fileName || "File"}
                    className="w-full h-56 object-cover rounded"
                    onError={(e) => {
                        e.target.src = "/fallback-image.png";
                    }}
                />
            );
        }

        const iconClass = "text-6xl mx-auto text-gray-400 my-8";

        switch (true) {
            case file?.fileType === "application/pdf":
                return <AiOutlineFilePdf className={iconClass} />;
            case file?.fileType?.includes("word"):
                return <AiOutlineFileWord className={iconClass} />;
            case file?.fileType?.includes("excel") || file?.fileType?.includes("spreadsheet"):
                return <AiOutlineFileExcel className={iconClass} />;
            case file?.fileType?.includes("text"):
                return <AiOutlineFileText className={iconClass} />;
            default:
                return <FiFile className={iconClass} />;
        }
    };

    const handleDownload = async () => {
        if (!file) return;

        const { signedUrl, fileName, signedThumbUrl } = file;
        const urlToFetch = signedUrl || signedThumbUrl;

        if (!urlToFetch) {
            setErrorMessage("No file URL available for download.");
            return;
        }

        try {
            const response = await fetch(urlToFetch, {
                method: "GET",
                headers: {},
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || "attachment";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            setErrorMessage(`Failed to download the file: ${error.message}`);
        }
    };

    const handleDelete = async () => {
        if (!file) return;

        const { mediaId, localId } = file;
        if (!mediaId && !localId) {
            setErrorMessage("No media information available for deletion.");
            setIsConfirmModalOpen(false);
            return;
        }

        if (localId && file.isLocal) {
            onRemoveImage(localId);
            onClose();
            setIsConfirmModalOpen(false);
            return;
        }

        if (!mediaId || !accountId) {
            setErrorMessage("No media or account information available for deletion.");
            setIsConfirmModalOpen(false);
            return;
        }

        try {
            setIsDeleting(true);
            setErrorMessage(null);
            const res = await deleteMedia(accountId, mediaId);
            if (res.status === 204) {
                onRemoveImage(mediaId);
                onClose();
                setIsConfirmModalOpen(false);
            } else {
                setErrorMessage(res.data?.message || "Failed to delete media.");
            }
        } catch (error) {
            console.error("Error deleting media:", error);
            setErrorMessage("An error occurred while deleting the media.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleConfirmDelete = () => {
        handleDelete();
    };

    return (
        <div className={`${collapsed ? 'md:w-[25%] w-[75%]' : 'md:w-[30%] w-[75%]'} h-full border-l relative bg-[#F9FAFC]`}>
            {errorMessage && !isConfirmModalOpen && (
                <div className="absolute top-12 right-6 text-red-600 bg-red-100 px-4 py-2 rounded-md">
                    {errorMessage}
                </div>
            )}

            <div className="md:p-6 p-3">{getFileIcon()}</div>

            <div className="flex items-center justify-between w-full">
                <h2 className="font-bold break-all text-lg  md:px-6 px-3">
                    {file ? (file.fileName?.split(".")[0] || "Unnamed File") : "No file selected"}
                </h2>
                {file && (
                    <div className="relative md:mr-6 mr-3 ">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleActionClick(file.mediaId || file.localId);
                            }}
                            className="p-1 rounded hover:bg-gray-200 bg-gray-200"
                        >
                            <FiMoreVertical className="text-gray-600 text-lg" />
                        </button>
                        {openMenuId === (file.mediaId || file.localId) && (
                            <div
                                ref={menuRef}
                                className="absolute right-5 w-24 bg-white rounded-md shadow-lg z-10 border border-gray-200 top-4"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={handleDownload}
                                >
                                    Download
                                </button>
                                <button
                                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                                    onClick={() => setIsConfirmModalOpen(true)}
                                >
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <h1 className="mt-3 font-bold text-lg md:px-6 px-3">Information</h1>

            <div className="mt-2 text-sm space-y-4 text-gray-700">
                <div className="flex items-center justify-between w-full border-b md:px-6 px-3 pb-3">
                    <strong>File Type:</strong> {file ? getFileType() : "--"}
                </div>
                <div className="flex items-center justify-between break-all w-full border-b md:px-6 px-3 pb-3">
                    <strong>Linked To:</strong> <span className="max-w-[75%]">{file ? (file.linkedType || "--") : "--"}</span>
                </div>
                <div className="flex items-center justify-between break-all w-full border-b md:px-6 px-3 pb-3">
                    <strong>Creator:</strong> <span className="max-w-[75%]">{file ? (file.creator.name || "--") : "--"}</span>
                </div>
                
                <div className="flex items-center justify-between w-full border-b md:px-6 px-3 pb-3">
                    <strong>Created On:</strong> {file ? (file.createdOn ? new Date(file.createdOn).toLocaleDateString() : "--") : "--"}
                </div>
                
            </div>

            <Modal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                    setErrorMessage(null);
                }}
                modalClass="xl:!w-[25%] lg:w-[32%] md:w-[40%] sm:w-[60%] w-[90%]"
            >
                <ConfirmationModal
                    confirmAction={"delete"}
                    setIsConfirmModalOpen={setIsConfirmModalOpen}
                    handleConfirm={handleConfirmDelete}
                    loading={isDeleting}
                    errorMessage={errorMessage}
                />
            </Modal>
        </div>
    );
};

export default FileDetails;